import { useEffect, useState } from 'react';
import { useAuth } from '@workos-inc/authkit-react';
import { api, bearer } from '../../api/client';
import { fetchMe } from '../../api/me';
import { productCatalogue } from '../../catalogue/products';
import type { EntitlementMap } from '../../types/entitlements';

export type UserStats = { active: number; invited: number; suspended: number; total: number };
export type ProductStats = { active: number; inactive: number; total: number };

export function deriveUserStats(statuses: string[]): UserStats {
  let active = 0;
  let invited = 0;
  let suspended = 0;
  for (const status of statuses) {
    if (status === 'Active') active++;
    else if (status === 'Invited') invited++;
    else if (status === 'Deactivated') suspended++;
  }
  return { active, invited, suspended, total: statuses.length };
}

export function deriveProductStats(products: EntitlementMap): ProductStats {
  // Count exactly what the Entitlements page this stat links to will list: products the
  // org holds an entitlement for. Admin-only tools aren't a licensable product, absent
  // products render as cross-sell (not as rows), and hidden ones never render at all.
  const visible = productCatalogue.filter((p) => {
    const e = products[p.key];
    return !p.requiresOrgAdmin && e !== undefined && e.status !== 'hidden';
  });
  const total = visible.length;
  const active = visible.filter((p) => products[p.key]?.status === 'active').length;
  return { active, inactive: total - active, total };
}

export type OrgStats = { loading: boolean; error: boolean; users: UserStats | null; products: ProductStats | null };

const INITIAL_STATE: OrgStats = { loading: true, error: false, users: null, products: null };

// The Overview tab unmounts on every navigation away; keeping the last successful stats
// here lets a return visit show data immediately while the refresh runs in the background.
let cachedStats: Pick<OrgStats, 'users' | 'products'> | null = null;

export function resetOrgStatsCache() {
  cachedStats = null;
}

export function useOrgStats(): OrgStats {
  const auth = useAuth();
  const [state, setState] = useState<OrgStats>(
    cachedStats ? { loading: false, error: false, ...cachedStats } : INITIAL_STATE,
  );

  useEffect(() => {
    const ctrl = new AbortController();
    void (async () => {
      try {
        const token = await auth.getAccessToken();
        if (!token) {
          setState({ loading: false, error: true, users: null, products: null });
          return;
        }
        const [usersRes, me] = await Promise.all([
          api.GET('/users', { headers: bearer(token), signal: ctrl.signal }),
          fetchMe(token, ctrl.signal),
        ]);
        if (usersRes.error || !usersRes.data) {
          setState({ loading: false, error: true, users: null, products: null });
          return;
        }
        const users = deriveUserStats(usersRes.data.users.map((u) => u.status));
        const products = deriveProductStats(me.products);
        cachedStats = { users, products };
        setState({ loading: false, error: false, users, products });
      } catch (e) {
        if ((e as Error).name !== 'AbortError') {
          setState({ loading: false, error: true, users: null, products: null });
        }
      }
    })();
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once on mount
  }, []);

  return state;
}
